import {
  useHtzAirHandlingUnitDataPutMutation,
  useHtzAirHandlingUnitDataShowQuery,
  useLazyHtzAirHandlingUnitDataListQuery,
} from "@/services/backend/htz/ahu/api/ahu-data-api";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGetAll,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  Query,
  useDocumentDBGet,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useAirHandlingUnitDataOffline(
  workOrderId: UUID,
  customerId: UUID,
) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzAirHandlingUnitDataListQuery();
  const [put] = useHtzAirHandlingUnitDataPutMutation();

  const prepMsg = t("Speichere RLT-Anlagen (Daten) ...");
  const syncMsg = t("Synchronisiere RLT-Anlagen (Daten) ...");
  const doneMsg = t("RLT-Anlagen (Daten)");

  const prepareAirHandlingUnitData = async () => {
    const result = await load({
      customer: { active: true, values: [customerId] },
    });
    if (result.error) {
      throw result.error;
    }

    addStale("airHandlingUnitData");
    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      result.data!.items.map(async (item) => {
        await documentDbPut<AirHandlingUnitData>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "airHandlingUnitData",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncAirHandlingUnitData = async () => {
    setSynced((prev) => [...prev, syncMsg]);

    const idbItems = await documentDbGetAll<AirHandlingUnitData>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "airHandlingUnitData",
    );

    await Promise.all(
      idbItems.map(async (item) => {
        await put(item).unwrap();
      }),
    );

    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { prepareAirHandlingUnitData, syncAirHandlingUnitData };
}

export function usePutAirHandlingUnitData(): Mutation<
  AirHandlingUnitData,
  AirHandlingUnitData
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [putMutation, { isLoading, error, isSuccess, reset }] =
    useHtzAirHandlingUnitDataPutMutation();

  const [
    putIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<AirHandlingUnitData, AirHandlingUnitData>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airHandlingUnitData",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [putMutation, { isLoading, error, isSuccess, reset }];
    case OfflineState.OFFLINE:
      return [
        putIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of air handling unit data not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetAirHandlingUnitData(
  id: string,
): Query<AirHandlingUnitData> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzAirHandlingUnitDataShowQuery({ id });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<AirHandlingUnitData>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "airHandlingUnitData",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
